import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import UploadProgressDisplay from "../common/UploadProgressDisplay";

const TerrainGeneration = ({ projectId }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressStatus, setProgressStatus] = useState({});
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Function to handle terrain generation
  const generateTerrain = async () => {
    try {
      setIsGenerating(true);
      setProgressStatus({
        "Terrain Generation": "Initializing terrain generation...",
      });
      setError(null);

      // Make API call to start terrain generation
      await apiClient.post(
        "/api/terrain/generate",
        { projectId },
        {
          useOperational: true,
        }
      );

      // Poll for status until completion or error
      let completed = false;
      while (!completed) {
        // Wait 3 seconds between status checks
        await new Promise((resolve) => setTimeout(resolve, 3000));

        // Check terrain generation status
        const statusResult = await apiClient.get(`/api/terrain/status`, {
          params: { projectId },
          useOperational: true,
        });

        if (statusResult.status === "done") {
          setProgressStatus({ "Terrain Generation": "Complete" });
          setStatus("Terrain generation completed successfully!");
          completed = true;
        } else if (statusResult.status === "error") {
          setProgressStatus({ "Terrain Generation": "Failed" });
          setError(
            statusResult.errorMessage ||
              "An error occurred during terrain generation"
          );
          completed = true;
        } else {
          // Update progress status message with current progress if available
          if (statusResult.progressMessage) {
            setProgressStatus({
              "Terrain Generation": statusResult.progressMessage,
            });
          }
        }
      }
    } catch (err) {
      console.error("Error generating terrain:", err);
      setProgressStatus({ "Terrain Generation": "Failed" });
      setError(err.message || "Failed to generate terrain");
    } finally {
      setIsGenerating(false);
    }
  };

  // If generation completes successfully, navigate back to menu after a short delay
  useEffect(() => {
    let timer;
    if (status && status.includes("completed successfully") && !isGenerating) {
      timer = setTimeout(() => {
        navigate("/menu");
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [status, isGenerating, navigate]);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="text-center mb-8">
        <img
          src="/static/images/logo.jpg"
          alt="Logo"
          className="mx-auto w-32 h-32 object-contain mb-4"
        />
        <h1 className="text-3xl font-bold text-gray-800">Terrain Generation</h1>
      </div>

      <div className="mb-8 text-center">
        <p className="text-gray-600 mb-2">
          Click the button to generate the STL file for the terrain
        </p>
        <p className="text-red-600 font-semibold">
          The process could take some minutes. Please do not leave this page.
          You will be automatically redirected when complete.
        </p>
      </div>

      {isGenerating ? (
        <div className="text-center p-8">
          <UploadProgressDisplay progress={progressStatus} />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : status && status.includes("completed successfully") ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6 text-center">
          <p className="text-green-700">{status}</p>
          <p className="text-sm text-gray-600 mt-2">
            Redirecting back to menu...
          </p>
        </div>
      ) : null}

      <div className="flex justify-between">
        <button
          onClick={() => navigate("/menu")}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
        >
          Back to Menu
        </button>

        {!isGenerating && !status?.includes("completed successfully") && (
          <button
            onClick={generateTerrain}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            disabled={isGenerating}
          >
            Generate Terrain
          </button>
        )}
      </div>
    </div>
  );
};

export default TerrainGeneration;
